import { ErrorMessage, PageContainer, Spinner, Pagination } from "@/components";
import SearchInput from "@/components/Search";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useBookmarkToggle } from "@/hooks/api/bookmark.queries";
import { useInternshipsQuery, useActiveInternshipQuery } from "@/hooks/api/internship.queries";
import {
  Bookmark,
  Briefcase,
  Building2,
  MapPin,
  Clock,
  Wifi,
  IndianRupee,
  ChevronRight,
  SlidersHorizontal,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function DiscoverInternships() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    domain: "",
    location: "",
    is_remote: false,
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [activeFilters, setActiveFilters] = useState({ page: 1, limit: 20 });

  const { data: activeInternship } = useActiveInternshipQuery();

  const { data, isLoading, isError, refetch } = useInternshipsQuery({
    ...activeFilters,
    search: appliedFilters.search || undefined,
    domain: appliedFilters.domain || undefined,
    location: appliedFilters.location || undefined,
    is_remote: appliedFilters.is_remote ? true : undefined,
  });

  const internships = data?.internships || [];
  const metaData = data?.metaData as any;
  const totalEntries = metaData?.pagination?.total || metaData?.total || internships.length;
  const totalPages = metaData?.pagination?.totalPages || metaData?.totalPages || Math.ceil(totalEntries / activeFilters.limit) || 1;

  const applyFilters = () => {
    setAppliedFilters(filters);
    setActiveFilters((prev) => ({ ...prev, page: 1 }));
  };
  const clearFilters = () => {
    const defaultFilters = { search: "", domain: "", location: "", is_remote: false };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setActiveFilters({ page: 1, limit: 20 });
  };

  if (isError) {
    return <ErrorMessage message="Failed to load internships." onRetry={() => refetch()} />;
  }

  return (
    <PageContainer className="min-h-screen">
      {/* Hero Header */}
      <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 p-8 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-yellow-300" />
            <span className="text-sm font-semibold text-indigo-200 uppercase tracking-widest">Opportunities</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-2">Internship Discovery</h1>
          <p className="text-indigo-200 text-base max-w-xl">
            Browse exclusive internship openings published by our top recruiter partners.
          </p>
          <div className="mt-5 flex items-center gap-3 text-sm text-indigo-200">
            <span className="flex items-center gap-1.5"><Briefcase size={14} />{isLoading ? "..." : internships.length} Openings</span>
            <span className="w-1 h-1 bg-indigo-400 rounded-full" />
            <span className="flex items-center gap-1.5"><Wifi size={14} />Remote Available</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Filters */}
        <aside className="w-full md:w-72 shrink-0">
          <div className="sticky top-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={16} className="text-indigo-600" />
                <h2 className="font-bold text-gray-900">Filters</h2>
              </div>
              <button
                onClick={clearFilters}
                className="text-xs text-gray-400 hover:text-red-500 transition flex items-center gap-1"
              >
                <X size={12} /> Clear
              </button>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search</Label>
              <SearchInput
                placeholder="Role, keywords..."
                value={filters.search}
                onValueChange={(val) => setFilters({ ...filters, search: val })}
                className={{
                  inputGroupClass: "rounded-xl border border-gray-200 focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100 bg-white transition-all",
                  inputClass: "text-sm focus-visible:ring-0",
                }}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Domain</Label>
              <Input
                placeholder="e.g. Software, Finance"
                value={filters.domain}
                onChange={(e) => setFilters({ ...filters, domain: e.target.value })}
                className="rounded-xl border-gray-200 focus:border-indigo-400 focus:ring-indigo-100"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</Label>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="e.g. Chennai, Remote"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  className="pl-9 rounded-xl border-gray-200 focus:border-indigo-400 focus:ring-indigo-100"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <Label htmlFor="remote-toggle" className="font-semibold text-gray-800 cursor-pointer">Remote Only</Label>
                <p className="text-xs text-gray-400 mt-0.5">Work from anywhere</p>
              </div>
              <Switch
                id="remote-toggle"
                checked={filters.is_remote}
                onCheckedChange={(v) => setFilters({ ...filters, is_remote: v })}
              />
            </div>

            <Button
              onClick={applyFilters}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold py-2.5"
            >
              Apply Filters
            </Button>
          </div>
        </aside>

        {/* Results Grid */}
        <div className="flex-1 min-w-0">
          {activeInternship?.has_active_internship && (
            <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Briefcase size={16} className="text-amber-600" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-amber-900">You have an active placement</h4>
                <p className="text-xs text-amber-700 mt-0.5">
                  You have already accepted an offer at <strong>{activeInternship.offer?.company_name || "another company"}</strong>.
                  Further applications may be restricted by your college placement cell.
                </p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <Spinner />
              <p className="text-gray-400 text-sm animate-pulse">Finding the best opportunities for you...</p>
            </div>
          ) : internships.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center bg-white rounded-2xl border border-dashed border-gray-200">
              <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-5">
                <Briefcase size={32} className="text-indigo-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No openings found</h3>
              <p className="text-gray-400 text-sm max-w-xs">Try adjusting your search filters to explore more roles.</p>
              <button onClick={clearFilters} className="mt-6 text-indigo-600 text-sm font-semibold hover:underline">
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {internships.map((job: any) => (
                  <InternshipCard key={job.id || job._id} job={job} onNavigate={navigate} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="mt-8 mb-4">
                  <Pagination
                    page={activeFilters.page}
                    totalPages={totalPages}
                    totalEntries={totalEntries}
                    currentCount={internships.length}
                    onPageChange={(page) => {
                      setActiveFilters((prev) => ({ ...prev, page }));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

// ─── Card sub-component (needs its own scope to call hooks per item) ──────────
function InternshipCard({ job, onNavigate }: { job: any; onNavigate: (path: string) => void }) {
  const id = job.id || job._id;
  const title = job.title || job.position || "Untitled Position";
  const company = job.company_name || job.company || "Confidential";
  const location = job.location || (job.is_remote ? "Remote" : "Any Location");
  const { isBookmarked, toggle, isPending } = useBookmarkToggle(id);

  return (
    <div
      className="group relative bg-white rounded-2xl border border-gray-100 p-6 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={() => onNavigate(`/discover/internships/${id}`)}
    >
      {/* Hover glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-violet-50/0 group-hover:from-indigo-50/60 group-hover:to-violet-50/30 transition-all duration-300 rounded-2xl" />

      <div className="relative">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-xl flex items-center justify-center shrink-0">
            <Building2 size={20} className="text-indigo-600" />
          </div>
          <div className="flex gap-2 flex-wrap items-center justify-end">
            {job.status === "open" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest rounded-full border border-emerald-100">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Hiring
              </span>
            )}
            {job.is_remote && (
              <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-widest rounded-full border border-blue-100">
                Remote
              </span>
            )}
            {/* Bookmark toggle */}
            <button
              onClick={(e) => { e.stopPropagation(); toggle(); }}
              disabled={isPending}
              title={isBookmarked ? "Remove bookmark" : "Save internship"}
              className={`p-1.5 rounded-lg transition-all ${isBookmarked
                ? "text-yellow-500 bg-yellow-50 hover:bg-yellow-100"
                : "text-gray-300 hover:text-yellow-400 hover:bg-yellow-50"
                } ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Bookmark size={16} className={isBookmarked ? "fill-yellow-400" : ""} />
            </button>
          </div>
        </div>

        <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-700 transition-colors mb-1 line-clamp-2">
          {title}
        </h3>
        <p className="text-sm text-gray-500 font-medium mb-4">{company}</p>

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-500 mb-5">
          <span className="flex items-center gap-1.5">
            <MapPin size={13} className="text-gray-400" /> {location}
          </span>
          {job.duration_weeks && (
            <span className="flex items-center gap-1.5">
              <Clock size={13} className="text-gray-400" /> {job.duration_weeks}w
            </span>
          )}
          {(job.stipend_min || job.stipend_max) && (
            <span className="flex items-center gap-1.5 text-emerald-600 font-semibold">
              <IndianRupee size={13} /> {job.stipend_min?.toLocaleString()}{job.stipend_max ? `–${job.stipend_max?.toLocaleString()}` : ""}/mo
            </span>
          )}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {job.domain && (
            <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
              {job.domain}
            </span>
          )}
          {job.required_skills?.slice(0, 2).map((s: string, i: number) => (
            <span key={i} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
              {s}
            </span>
          ))}
          {job.required_skills?.length > 2 && (
            <span className="px-3 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
              +{job.required_skills.length - 2}
            </span>
          )}
        </div>

        {/* CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-400">
            {job.openings ? `${job.openings} opening${job.openings > 1 ? "s" : ""}` : "Open Positions"}
          </span>
          <span className="flex items-center gap-1 text-indigo-600 text-sm font-semibold group-hover:gap-2 transition-all">
            View Details <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </div>
  );
}

